/* =====================================================================
   EverMount fountain PDP - root zoom scaling
   Split out of wt-fountain.js and loaded WITHOUT defer by
   sections/wt-fountain-boot.liquid, because it has to set the zoom before
   the browser paints the product section. Everything else on this page can
   wait for DOMContentLoaded; this cannot.

   It reads --wt-band, which the boot section declares inline, so CSS and JS
   can never disagree about which device band the page is in.
   ===================================================================== */

/* ===================== page-scale ===================== */
/* MOBILE PAGE SCALE (owner 2026-09-10). The mobile type scale in the type-scale-mobile block
   was tuned as ONE composition; its proportions must hold on every phone. The narrowest
   viewport at which that composition lays out with none of its no-wrap rules breaking
   (benefit bullets, kit titles, filter name, Total row, delivery ETA, trust labels, title) is
   438px with the Starter Kit selected and 448px with the 90-Day Hydration Kit ("Total today:
   $200 $109.99 SAVE 45%" is the widest state), so 450px is the reference. On any narrower
   phone the whole document is zoomed by width / 450, which makes the layout viewport exactly
   450 CSS px wide: every element, gap, image and line break is the reference one, just drawn
   smaller — 390 draws at 0.867, 375 at 0.833, 360 at 0.8. At 450px and wider (and on
   desktop) the zoom is 1 and nothing changes.
   Viewport units and env() insets are not zoomed by the browser, so the few modal rules that
   use vw / vh read --wt-vw / --wt-vh instead, and the two safe-area paddings divide by
   --wt-zoom; all three are kept in the zoomed coordinate space here.
   Runs before first paint, and again on resize / orientation change. */
(function(){
  /* This file can be linked twice - once from snippets/wt-fountain-head.liquid and once
     from sections/wt-fountain-boot.liquid - so that neither path is a hard dependency.
     The browser serves the second <script src> from cache, but it still EXECUTES it, and
     running the scaler twice would install a second resize listener and double the work
     on every resize. Same URL, one request, one run. */
  if (window.__wtScaleInit) return;
  window.__wtScaleInit = 1;

  /* Debug switches, honoured on both load paths (a section setting could not be, because
     the head snippet renders before any section does):
       ?wt-scale=off   draw the page at its natural width, no root zoom
       ?wt-fit=off     stop the measured type fitting in wt-fountain.js
     Both also accept data-wt-scale / data-wt-fit on <html> for testing from the console. */
  try {
    var qs = new URLSearchParams(window.location.search);
    if (qs.get('wt-fit') === 'off') document.documentElement.setAttribute('data-wt-fit', 'off');
    if (qs.get('wt-scale') === 'off') { document.documentElement.setAttribute('data-wt-scale', 'off'); return; }
  } catch (e) { /* no URLSearchParams: carry on with scaling enabled */ }
  if (document.documentElement.getAttribute('data-wt-scale') === 'off') return;

  /* Three references, one mechanism (tablet band added 2026-09-16). Each is the narrowest
     width at which THAT composition lays out with none of its no-wrap lines breaking, in both
     kit states, measured with the fit script disabled (data-wt-fit="off"):
       REF_PHONE   450  single column, phone type scale      -> phones, below 700
       REF_TABLET  960  two-column grid, phone type scale    -> tablet portrait, 700-999
       REF_DESK   1366  two-column grid, desktop type scale  -> 1000 and up, touch only
     Anything narrower than its own reference is zoomed by width / reference, so it draws that
     reference layout exactly, only smaller; anything at or above its reference stays at 1.

     REF_DESK was 1070 (the width at which the buy box alone stops breaking) and that was too
     narrow: the desktop composition is not "desktop-shaped" there. Owner review on an iPad Pro
     13 listed what still looked wrong, and every item was the same cause — a 1070-wide desktop
     layout. Scanning the desktop composition width by width, 1320 is the first width where all
     of them match desktop at once:
         width  title  stock/ETA  story heads  thumb rail  cross-sell name
         1070    3L      2 rows     3L          +127px       1L
         1150    3L      1 row      2L          +83px        2L
         1200    2L      1 row      2L          +57px        2L
         1240    2L      1 row      2L          +36px        1L
         1320    2L      1 row      2L           -5px        1L   <- first all-clear
         1366    2L      1 row      2L          -29px        1L
     ("thumb rail" is how far the five-thumbnail rail hangs below the main image; negative means
     it no longer does.) 1366 is taken rather than 1320 for margin, and because it is the iPad
     Pro 13's own landscape width — so that iPad renders one identical composition in both
     orientations and rotating it changes only the scale, never the layout.
     Worked values: iPhone 390 -> 0.867 (unchanged), iPad Pro 11 portrait 834 -> 0.869,
     iPad Pro 13 portrait 1024 -> 0.750, iPad Pro 11 landscape 1194 -> 0.874, iPad Pro 13
     landscape 1366 -> 1, desktop 1440 -> 1.

     A fourth band was added 2026-09-16 and is deliberately NOT a reference: tablet portrait
     700-799 (iPad mini) draws the single column unscaled, at z = 1. See the MQ_MINI note below
     for why a reference would be wrong there. */
  var REF_PHONE = 450, REF_TABLET = 960, REF_DESK = 1366, REF_SURFACE = 780;
  var root = document.documentElement;
  /* The band is decided by the SAME media queries the stylesheets use, asked of the same
     engine, so the CSS that applies and the reference we scale to can never disagree. Reading
     it off a number instead (clientWidth, or `base` below) drifts from the CSS in two real
     cases: fractional viewport widths at 699.5 / 999.5, and browser page zoom, where `base` is
     deliberately larger than the viewport.
     MQ_TALL is what separates a tablet from a PHONE IN LANDSCAPE: an iPhone 15 Pro Max on its
     side is 932 CSS px wide with a coarse pointer, which is indistinguishable from a tablet by
     width and pointer alone. Its SHORT side is ~430, while every iPad is at least 744, so a
     600px floor on the viewport height splits them cleanly. The tablet stylesheet carries the
     same (min-height:600px) guard. */
  var MQ_WIDE   = window.matchMedia('(min-width:700px)');
  /* 999.99, not 999: at a fractional viewport of 999.4 — which coarse devices do hit at
     fractional DPR — `(max-width:999px)` is FALSE while the CSS `(max-width:999px)` blocks are
     also false and the `(min-width:1000px)` blocks are still false. Asking for 999px here put
     the script in the desk band and wrote zoom 0.7317 while every band stylesheet was off, so
     the theme's base single-column stack was laid out at 1366px and scaled down. Widening the
     JS query to 999.99 keeps the script on the tablet side for the whole seam. */
  var MQ_NARROW = window.matchMedia('(max-width:999.99px)');
  /* MQ_STACK picks out the widths inside the 700-999 tablet band that draw ONE column instead
     of the two-column grid. It is only ever consulted once the band is already known to be
     700-999, so its two clauses mean 700-799 and 900-999.
       700-799  iPad mini portrait (744, 768)          single column
       800-899  iPad portrait (810, 820, 834)          two columns
       900-999  Surface portrait (912, 960)            single column
     Both single-column ranges were owner calls (2026-09-16) against the Petlibro reference he
     supplied, which draws one column at 744, 834 AND 960 and only goes two-column at 1024. He
     kept the two-column layout he had already approved for iPad portrait and took the single
     column for the two ranges on either side of it, so the band is deliberately not monotonic.
     The 900 boundary is where it is because no iPad portrait width falls above it and no
     Surface portrait width falls below it.
     The 700-799 range has NO reference and is never zoomed (900-999 is different: its band
     is `surface`, mapped to REF_SURFACE below and scaled UP by base/780 - see the REF_SURFACE
     note). The single column here is the phone
     composition, and the phone composition is fluid above its own 450 reference — it is only
     ever scaled DOWN, to stop lines breaking on narrow phones. Pointing 744 at REF_PHONE would
     have to zoom UP by 1.65x, which magnifies every glyph instead of laying the column out
     wider; pointing it at REF_TABLET 960 would lay out a 960-wide single column and then shrink
     it. Leaving z = 1 lets the column fill the viewport at native size, which is what the
     reference page does and what the theme's own <=999 rules already draw. The measure of the
     buy box is held by a max-width in tablet-mini-single rather than by scaling. */
  /* 799.99, not 799, for the same reason MQ_NARROW is 999.99 above: at a fractional width in
     (799,800) both a `max-width:799px` and a `min-width:800px` query are false, so the script
     and the stylesheets would land in different bands. The CSS ranges carry the matching
     799.99 / 899.99 bounds. */
  var MQ_MINI   = window.matchMedia('(max-width:799px)');
  var MQ_SURF   = window.matchMedia('(min-width:900px)');
  /* These five are CHANGE DETECTORS only — nothing reads .matches to pick a band any more, that
     comes from --wt-band. They exist so the script re-runs the moment any input the band depends
     on flips, including (pointer:coarse) changing with no resize event at all when a keyboard is
     docked or a mouse is paired. */
  var MQ_TALL   = window.matchMedia('(min-height:600px)');
  var MQ_COARSE = window.matchMedia('(pointer:coarse)');
  var CAN_ZOOM = !!(window.CSS && CSS.supports && CSS.supports('zoom', '1'));
  var last = {};
  function put(prop, val){            /* write only on change: resize fires on every URL-bar collapse */
    if(last[prop] === val) return; last[prop] = val;
    if(val === null) root.style.removeProperty(prop); else root.style.setProperty(prop, val);
  }
  function apply(){
    /* Layout viewport, not window.innerWidth: on iOS innerWidth follows the VISUAL viewport,
       so a pinch or the 16px focus auto-zoom would feed a smaller width back in here. */
    var w = root.clientWidth || window.innerWidth, h = root.clientHeight || window.innerHeight;
    /* Browser page zoom (Safari's Aa menu, Chrome's zoom) shrinks the CSS-px viewport; the
       device's short side (screen.*) does not move with it. Sizing from the larger of the two
       means a reader who zooms to 150% gets 150% on top of the phone's normal scale, instead
       of the page counter-zooming their choice away. Landscape and desktop windows give 1. */
    /* Only trust the screen when the window fills it: a narrow window on a big display
       (iPad Slide Over / Split View, Android split-screen, a narrowed desktop window) is a
       genuinely narrow viewport and must be scaled, not mistaken for page zoom. */
    /* The screen is only a trustworthy proxy for the device when the page owns the window
       and the screen is phone-sized. In an <iframe> (the theme editor's phone preview) and on
       a desktop monitor, screen.* describe the monitor, not the viewport: the editor's 375px
       preview read screen 1440x900 through its frame, base became 900, z stayed 1 and the
       450 composition was drawn into 375px - overflowing buy box, clipped sticky bar. */
    var framed = true;
    try { framed = window.self !== window.top; } catch (e) { framed = true; }
    var fillsScreen = !framed && (window.outerWidth || w) >= (window.screen.width || 0) - 2;
    var shortSide = Math.min(window.screen.width || w, window.screen.height || w);
    var device = (fillsScreen && shortSide <= 600) ? shortSide : w;
    /* `base` is computed per band, below, because the `device` term is only sound for
       REF_PHONE. See the note under `var REF`. */
    /* REF 0 means "never scale". Below 700 is the phone band, unchanged. At 700 and above only
       a touch device tall enough to be a tablet is scaled; everything else (a mouse-driven
       desktop at any width, a phone on its side) falls through to 0 and keeps z = 1, which is
       exactly what the old single-reference script produced there. */
    /* The band is read from the stylesheet (band-marker block above), never re-derived here.
       See that block for why: two hand-kept copies of the same queries drifted twice, and the
       fractional bounds that were meant to fix it round up in Chrome. An unrecognised value —
       including `none` at a fractional width no band claims — gives REF 0, which means no zoom
       and the theme's own layout. */
    var band = (getComputedStyle(root).getPropertyValue('--wt-band') || '').trim();
    var REF = band === 'phone'   ? REF_PHONE
            : band === 'tablet'  ? REF_TABLET
            : band === 'surface' ? REF_SURFACE
            : band === 'desk'    ? REF_DESK
            : 0;
    /* REF_PHONE is a CEILING — 450 is wider than any phone, so `base >= REF` just means the
       viewport is at least as roomy as the reference and z = 1 costs nothing. That is what
       makes the page-zoom `device` term safe there.
       REF_TABLET and REF_DESK are MINIMA — 960 is the narrowest width at which the two-column
       composition stops breaking. Feeding them a `base` larger than the real viewport makes
       `base < REF` false, so z stays 1 and the 960 composition is laid out in a viewport
       narrower than 960, which overflows. Concretely: an iPad Pro 13 (screen short side 1024)
       at Safari 115% page zoom has w = 890 and device = 1024, so base = 1024, z = 1, and the
       buy box overflows. In the tablet and desk bands the viewport width is the only honest
       input. */
    var base = (REF === REF_PHONE) ? Math.max(w, device) : w;
    /* REF_SURFACE is the one reference the page scales UP to, not down (owner 2026-09-16:
       "the fonts seem too small" on a Surface Pro 10). Laying the single column out at 880 and
       painting it at the device width makes every glyph, gap and rule 9% larger at 960 while
       the composition is bit-for-bit the one drawn at 880 — proportions cannot drift, because
       only the scale factor changes.
       880 was tried first (+9%) and the owner still read the type as too small. The reference
       page he is comparing against sets 16px body copy at 960 while 880 gave us 14.2px, so 780
       is chosen to land our body copy on that same 16px. Cost, measured by counting line boxes
       over every text node: 940 is the last width at which nothing rewraps at all; 880 costs two
       body paragraphs; 780 costs eleven text nodes, all of them either paragraphs inside the
       DESCRIPTION / FEATURES / SHIPPING accordions or names on cross-sell product cards. Nothing
       structural breaks at 780 — no buy-box heading, price, button, kit tile, stock/ETA row or
       trust label gains a line at any scale down to 760.
       Every other band still only scales DOWN: their references are the narrowest width at
       which that composition holds, so scaling up would just magnify a layout that already
       fits. */
    var z = (CAN_ZOOM && REF && (base < REF || REF === REF_SURFACE)) ? base / REF : 1;
    if(z === 1){
      put('zoom', null); put('--wt-zoom', null); put('--wt-vw', null); put('--wt-vh', null);
      if(root.hasAttribute('data-wt-zoomed')) root.removeAttribute('data-wt-zoomed');
    } else {
      if(!root.hasAttribute('data-wt-zoomed')) root.setAttribute('data-wt-zoomed', '');
      put('zoom', z.toFixed(4));
      put('--wt-zoom', z.toFixed(4));
      put('--wt-vw', (w / z / 100).toFixed(3) + 'px');
      put('--wt-vh', (h / z / 100).toFixed(3) + 'px');
    }
  }
  var raf = null;
  function schedule(){ if(raf) return; raf = requestAnimationFrame(function(){ raf = null; apply(); }); }
  apply();
  window.addEventListener('resize', schedule);
  window.addEventListener('orientationchange', schedule);
  /* Re-run whenever anything the band depends on flips. The pointer query matters on its own:
     docking a 2-in-1, pairing a mouse with an Android tablet or Chromebook, or toggling device
     emulation can change (pointer:coarse) with NO resize event, which would otherwise leave the
     stylesheet in one band and `zoom` stuck on the other's value. */
  [MQ_WIDE, MQ_NARROW, MQ_MINI, MQ_SURF, MQ_TALL, MQ_COARSE].forEach(function(m){
    if(m.addEventListener) m.addEventListener('change', schedule); else if(m.addListener) m.addListener(schedule);
  });
  window.__wtMobileScale = apply;

  /* POPUP SCROLL LOCK under the zoom (audit H1, 2026-10-15). Attentive's popup freezes the
     page with body{position:absolute; top:-<scrollY>px}. That offset is applied inside the
     zoomed root, so it moves the page by scrollY * zoom instead of scrollY, and the page
     behind the popup jumped (600px scrolled at 0.867 = an 80px jump, CLS 0.78). While a zoom
     is applied, a negative body top written by anyone else is rescaled to -scrollY / zoom,
     which lands the page exactly where it was. Attentive itself is untouched, and its unlock
     (clearing the style and scrolling back) works as before. */
  (function(){
    if (!window.MutationObserver) return;
    var mine = null;
    function fix(){
      var b = document.body;
      if (!b) return;
      var st = b.style, z = parseFloat(root.style.zoom) || 1;
      if (z === 1 || !/absolute|fixed/.test(st.position)) { mine = null; return; }
      var top = st.top;
      if (!top || top === mine) return;
      var px = parseFloat(top);
      if (!(px < 0) || !/px$/.test(top)) return;
      mine = (px / z).toFixed(2) + 'px';
      st.top = mine;
    }
    function start(){ new MutationObserver(fix).observe(document.body, { attributes: true, attributeFilter: ['style'] }); }
    if (document.body) start(); else document.addEventListener('DOMContentLoaded', start);
  })();

  /* ?wt-debug=1 : a small read-only overlay reporting what the scaler decided on THIS device
     (band, zoom, viewport, screen, pointer, framed). For diagnosing a report of "it looks
     wrong on my phone" without guessing at the device; it renders nothing otherwise. */
  try {
    var dbg = new URLSearchParams(window.location.search).get('wt-debug') === '1';
    if (dbg) {
      var hud = document.createElement('div');
      hud.setAttribute('data-wt-hud', '');
      hud.style.cssText = 'position:fixed;left:6px;top:6px;z-index:2147483647;background:rgba(32,32,32,.88);color:#fff;font:11px/1.4 ui-monospace,Menlo,monospace;padding:6px 8px;border-radius:6px;pointer-events:none;white-space:pre;max-width:90vw';
      var paint = function(){
        var cs = getComputedStyle(root);
        hud.textContent = 'band ' + (cs.getPropertyValue('--wt-band') || '').trim() + '  zoom ' + (cs.zoom || '1') +
          '\nviewport ' + root.clientWidth + 'x' + root.clientHeight + '  screen ' + screen.width + 'x' + screen.height +
          '\ncoarse ' + (MQ_COARSE.matches ? 'yes' : 'no') + '  framed ' + (window.self !== window.top ? 'yes' : 'no') +
          '  dpr ' + (window.devicePixelRatio || 1) + '\n' + navigator.userAgent.slice(0, 70);
      };
      var mount = function(){ (document.body || root).appendChild(hud); paint(); };
      if (document.body) mount(); else document.addEventListener('DOMContentLoaded', mount);
      window.addEventListener('resize', function(){ requestAnimationFrame(paint); });
    }
  } catch (e) { /* diagnostics only */ }
})();
